import { useMemo } from 'react';

import { useAtom } from 'jotai';

import { useFeatureSpend } from '../../../hooks/mmm/useFeatureSpend';
import { filterObjectKeysBySuffix } from '../../../utils/mmm/helpers';
import { formatName } from '../../../utils/valueFormatter';
import { startDateBaselineAtom, endDateBaselineAtom } from '../atoms';
import { OPTIMIZATION_OPTIONS, KPI_METRIC_TEXT } from '../constants';

export const useScenarios = isConversionKPI => {
  const scenarioName = useMemo(() => {
    const kpiText = isConversionKPI ? formatName(KPI_METRIC_TEXT.conversions) : formatName(KPI_METRIC_TEXT.revenue);
    const metricText = isConversionKPI ? KPI_METRIC_TEXT.CPA : KPI_METRIC_TEXT.ROAS;
    return (
      {
        [OPTIMIZATION_OPTIONS.maxKPIs]: `${kpiText} Maximization`,
        [OPTIMIZATION_OPTIONS.reduceBudget]: 'Budget Reduction',
        [OPTIMIZATION_OPTIONS.expandBudget]: 'Budget Expansion',
        [OPTIMIZATION_OPTIONS.targetMetric]: `${metricText} Target`,
      }
    );
  }, [isConversionKPI]);

  return {
    scenarioName,
  };
};

export const useFilteredData = (responseCurvesRaw, trainingDataRaw, trainingStatsRaw, filterBy) => {
  const responseCurves = useMemo(() => {
    if (!responseCurvesRaw) return responseCurvesRaw;
    return filterObjectKeysBySuffix(responseCurvesRaw, filterBy);
  }, [responseCurvesRaw]);

  const trainingData = useMemo(() => {
    if (!trainingDataRaw) return trainingDataRaw;
    return trainingDataRaw.map(data => filterObjectKeysBySuffix(data, filterBy));
  }, [trainingDataRaw]);

  const trainingStats = useMemo(() => {
    if (!trainingStatsRaw) return trainingStatsRaw;
    return trainingStatsRaw.filter(({ feature }) => !feature.endsWith(filterBy));
  }, [trainingStatsRaw]);

  return {
    responseCurves,
    trainingData,
    trainingStats,
  };
};

export const useFeatureStats = ({ trainingData, trainingDateRange, responseCurveFeatures }) => {
  const [startDate] = useAtom(startDateBaselineAtom);
  const [endDate] = useAtom(endDateBaselineAtom);
  const { featureStats, loading } = useFeatureSpend({ trainingData, trainingDateRange, responseCurveFeatures, startDate, endDate });

  return {
    featureStats,
    loadingCube: loading,
  };
};
